import { useEffect, useState } from 'react';
import { SaveError } from './SaveError';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { useAuth } from '@shared/auth/AuthContext';
import { useCreator } from './useCreator';
import CreatorMenu from './CreatorMenu';
import CreatorClaim from './CreatorClaim';
import { igConfigured, startIgConnect } from '@shared/lib/igConnect';

interface ProfileRow {
  country: string[] | null; content_languages: string[] | null; niche_ids: string[] | null; accepts_barter: boolean | null;
  open_to_collab: boolean | null;
  contact_phone: string | null; contact_whatsapp: string | null; contact_telegram: string | null; contact_email: string | null;
}
interface Opt { value: string; label: string }

const COUNTRIES: Opt[] = [
  { value: 'KG', label: 'Кыргызстан' }, { value: 'KZ', label: 'Казахстан' }, { value: 'UZ', label: 'Узбекистан' },
  { value: 'TJ', label: 'Таджикистан' }, { value: 'RU', label: 'Россия' }, { value: 'TR', label: 'Турция' },
  { value: 'AE', label: 'ОАЭ' }, { value: 'US', label: 'США' }, { value: 'KR', label: 'Корея' }, { value: 'CN', label: 'Китай' },
];
const LANGS: Opt[] = [
  { value: 'ky', label: 'Кыргызча' }, { value: 'ru', label: 'Русский' }, { value: 'en', label: 'English' },
  { value: 'kk', label: 'Қазақша' }, { value: 'uz', label: 'Ўзбекча' }, { value: 'tr', label: 'Türkçe' },
];

// Loud blue chip = interactive picker (edit mode). Quiet chip = read-only display.
const chipStyle = (on: boolean): React.CSSProperties => ({
  fontSize: 12.5, padding: '6px 11px', borderRadius: 18, cursor: 'pointer', userSelect: 'none', fontFamily: 'inherit', minHeight: 36,
  color: on ? '#bcd0ff' : '#99a', background: on ? 'rgba(74,158,255,0.16)' : 'rgba(255,255,255,0.04)',
  border: '1px solid ' + (on ? 'rgba(74,158,255,0.45)' : 'rgba(255,255,255,0.1)'),
});
const readChip: React.CSSProperties = {
  fontSize: 12.5, padding: '5px 10px', borderRadius: 8, color: '#c2cede',
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
};
const card: React.CSSProperties = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '4px 14px', marginBottom: 12 };
const sectionLabel: React.CSSProperties = { color: 'var(--muted)', fontSize: 13, fontWeight: 600, margin: '14px 2px 6px' };

const smallBtn: React.CSSProperties = { background: 'rgba(74,158,255,0.16)', border: '1px solid rgba(74,158,255,0.45)', color: '#bcd0ff', borderRadius: 8, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' };
const ghostBtn: React.CSSProperties = { background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', color: '#cdd', borderRadius: 8, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' };
const editLink: React.CSSProperties = { background: 'transparent', border: 'none', color: '#7fa8e0', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', padding: 0 };

// One independently-editable field (country / language / niche) rendered as a ROW inside a
// shared card: collapsed to the chosen values, expands to the picker on Изменить.
function FieldRow({ providerId, title, dbField, options, current, searchable, isNiche, last, onSaved }:
  { providerId: string; title: string; dbField: string; options: Opt[]; current: string[]; searchable?: boolean; isNiche?: boolean; last?: boolean; onSaved: () => void }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const invalidate = useInvalidateSupabase();
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState<string[]>(current);
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(false);
  useEffect(() => { setSel(current); }, [current]);

  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const toggle = (v: string) => setSel((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v]);
  const shown = searchable ? options.filter((o) => !filter || o.label.toLowerCase().includes(filter.toLowerCase())) : options;

  const save = async () => {
    setSaving(true); setErr(false);
    const { error } = await supabase.from('instagram_providers').update({ [dbField]: sel }).eq('id', providerId);
    setSaving(false);
    if (error) { setErr(true); return; }
    onSaved(); setEditing(false);
    if (isNiche) { invalidate(['creator-digest']); invalidate(['creator-collabs']); invalidate(['creator-pitch']); invalidate(['creator-peers']); }
  };

  const rowStyle: React.CSSProperties = { padding: '13px 0', borderBottom: last ? undefined : '1px solid rgba(255,255,255,0.06)' };
  const heading = (right: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ color: '#aeb6c4', fontWeight: 600, fontSize: 13 }}>
        {title}{isNiche && (editing || current.length) ? <span style={{ color: '#66707f', fontWeight: 400 }}> · {editing ? sel.length : current.length}</span> : null}
      </span>
      {right}
    </div>
  );

  if (!editing) {
    return (
      <div style={rowStyle}>
        {heading(<button type="button" onClick={() => { setSel(current); setFilter(''); setEditing(true); }} style={editLink}>{tr('creator.profile.edit', 'Изменить')}</button>)}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {current.length
            ? current.map((v) => <span key={v} style={readChip}>{labelFor(v)}</span>)
            : <span style={{ color: 'var(--dim)', fontSize: 13 }}>{tr('creator.profile.notSet', 'Не указано')}</span>}
        </div>
      </div>
    );
  }

  return (
    <div style={rowStyle}>
      {heading(
        <span style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => { setSel(current); setEditing(false); }} style={ghostBtn}>{tr('creator.profile.cancel', 'Отмена')}</button>
          <button type="button" onClick={save} disabled={saving} style={smallBtn}>{saving ? '…' : tr('creator.profile.save', 'Сохранить')}</button>
        </span>,
      )}
      {searchable && (
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={tr('creator.profile.search', 'Поиск ниши…')}
          style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8, background: 'rgba(0,0,0,0.25)', color: 'var(--text)', border: '1px solid rgba(74,158,255,0.25)', fontSize: 14, marginBottom: 8, fontFamily: 'inherit' }} />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: searchable ? 200 : undefined, overflowY: searchable ? 'auto' : undefined }}>
        {shown.map((o) => <button type="button" key={o.value} aria-pressed={sel.includes(o.value)} onClick={() => toggle(o.value)} style={chipStyle(sel.includes(o.value))}>{o.label}</button>)}
      </div>
      {err && <SaveError />}
    </div>
  );
}

// Opt-in to receiving collab proposals from other creators. Optimistic; reverts on error.
function CollabToggle({ providerId, value, onSaved }: { providerId: string; value: boolean; onSaved: () => void }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const [on, setOn] = useState(value);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(false);
  useEffect(() => { setOn(value); }, [value]);
  const toggle = async () => {
    const next = !on;
    setOn(next); setSaving(true); setErr(false);
    const { error } = await supabase.from('instagram_providers').update({ open_to_collab: next }).eq('id', providerId);
    setSaving(false);
    if (error) { setOn(!next); setErr(true); return; }
    onSaved();
  };
  return (
    <label style={{ ...card, display: 'flex', alignItems: 'flex-start', gap: 10, padding: 14, cursor: saving ? 'default' : 'pointer' }}>
      <input type="checkbox" checked={on} onChange={toggle} disabled={saving} style={{ width: 18, height: 18, marginTop: 1, accentColor: 'var(--accent)', cursor: 'inherit', flexShrink: 0 }} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>{tr('creator.profile.openCollab', 'Открыт для коллабов')}</span>
        <span style={{ color: 'var(--dim)', fontSize: 12.5, lineHeight: 1.4 }}>{tr('creator.profile.openCollabHint', 'Другие блогеры смогут предлагать вам совместные проекты. Выключите, если не хотите получать предложения.')}</span>
        {err && <SaveError />}
      </span>
    </label>
  );
}

// Creator contact details brands can use to reach out. Free-text, all optional; saved on
// instagram_providers (owner_or_admin_update RLS), same write path as the fields above.
const CONTACTS: { key: keyof ProfileRow; label: string; labelKey?: string; ph: string; type?: string; hintKey?: string; hintDef?: string }[] = [
  { key: 'contact_phone', label: 'Телефон', labelKey: 'creator.profile.phone', ph: '+996 700 123456', type: 'tel', hintKey: 'creator.profile.fromAccount', hintDef: 'Из вашего аккаунта — можно изменить.' },
  { key: 'contact_whatsapp', label: 'WhatsApp', ph: '+996 700 123456', type: 'tel' },
  { key: 'contact_telegram', label: 'Telegram', ph: '@username' },
  { key: 'contact_email', label: 'Email', ph: 'you@mail.com', type: 'email', hintKey: 'creator.profile.fromAccount', hintDef: 'Из вашего аккаунта — можно изменить.' },
];
function ContactFields({ providerId, initial, onSaved }: { providerId: string; initial: Record<string, string>; onSaved: () => void }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState<Record<string, string>>(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(false);
  useEffect(() => { setVals(initial); }, [initial]);

  const anySet = CONTACTS.some((c) => (initial[c.key as string] ?? '').trim());
  const save = async () => {
    setSaving(true); setErr(false);
    const patch: Record<string, string | null> = {};
    for (const c of CONTACTS) { const v = (vals[c.key as string] ?? '').trim(); patch[c.key as string] = v || null; }
    const { error } = await supabase.from('instagram_providers').update(patch).eq('id', providerId);
    setSaving(false);
    if (error) { setErr(true); return; }
    onSaved(); setEditing(false);
  };

  const inputStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '10px 11px', borderRadius: 8, background: 'rgba(0,0,0,0.25)', color: '#eee', border: '1px solid rgba(74,158,255,0.25)', fontSize: 15, fontFamily: 'inherit' };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 0 8px' }}>
        <span style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>{tr('creator.profile.contacts', 'Контакты')}</span>
        {editing
          ? <span style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => { setVals(initial); setEditing(false); }} style={ghostBtn}>{tr('creator.profile.cancel', 'Отмена')}</button>
              <button type="button" onClick={save} disabled={saving} style={smallBtn}>{saving ? '…' : tr('creator.profile.save', 'Сохранить')}</button>
            </span>
          : <button type="button" onClick={() => { setVals(initial); setEditing(true); }} style={editLink}>{tr('creator.profile.edit', 'Изменить')}</button>}
      </div>

      {editing ? (
        <div style={{ display: 'grid', gap: 10, paddingBottom: 14 }}>
          {CONTACTS.map((c) => (
            <label key={c.key as string} style={{ display: 'block' }}>
              <span style={{ display: 'block', color: 'var(--dim)', fontSize: 12, marginBottom: 4 }}>{c.labelKey ? tr(c.labelKey, c.label) : c.label}</span>
              <input type={c.type ?? 'text'} value={vals[c.key as string] ?? ''} placeholder={c.ph}
                onChange={(e) => setVals((s) => ({ ...s, [c.key as string]: e.target.value }))} style={inputStyle} />
              {c.hintKey && (initial[c.key as string] ?? '').trim() && (
                <span style={{ display: 'block', color: '#66707f', fontSize: 11, marginTop: 3 }}>{tr(c.hintKey, c.hintDef ?? '')}</span>
              )}
            </label>
          ))}
          {err && <SaveError />}
        </div>
      ) : anySet ? (
        <div style={{ paddingBottom: 12 }}>
          {CONTACTS.filter((c) => (initial[c.key as string] ?? '').trim()).map((c) => (
            <div key={c.key as string} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', fontSize: 13.5 }}>
              <span style={{ color: 'var(--dim)' }}>{c.labelKey ? tr(c.labelKey, c.label) : c.label}</span>
              <span style={{ color: 'var(--text)', textAlign: 'right', wordBreak: 'break-all' }}>{initial[c.key as string]}</span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: 'var(--dim)', fontSize: 13, paddingBottom: 14, lineHeight: 1.4 }}>
          {tr('creator.profile.contactsHint', 'Добавьте, чтобы бренды могли связаться с вами напрямую.')}
        </div>
      )}
    </div>
  );
}

// The Profile tab: an "About" card (country / language / niche), contacts, the rate card
// (prices + barter), Instagram connect, and the delete-account danger zone.
export default function CreatorProfileTab() {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const { provider, verified, pendingClaim, refetch } = useCreator();
  const { user, signOut } = useAuth();
  // The «Подтвердить» banner links here with #verify — bring the code card into view.
  useEffect(() => {
    if (!verified && window.location.hash === '#verify') {
      const id = setTimeout(() => document.getElementById('verify')?.scrollIntoView({ block: 'start' }), 200);
      return () => clearTimeout(id);
    }
  }, [verified]);
  const rowQ = useSupabaseQuery<ProfileRow | null>(['creator-profile', provider?.id],
    async () => (provider
      ? supabase.from('instagram_providers')
          .select('country, content_languages, niche_ids, accepts_barter, open_to_collab, contact_phone, contact_whatsapp, contact_telegram, contact_email')
          .eq('id', provider.id).maybeSingle()
      : ({ data: null, error: null } as never)),
    { enabled: !!provider });
  // Phone captured at signup (may be null for email-OTP creators). Used only as a default
  // for the contact phone so the creator doesn't retype what we already hold.
  const profQ = useSupabaseQuery<{ phone: string | null } | null>(['my-profile-phone', user?.id],
    async () => (user ? supabase.from('profiles').select('phone').eq('id', user.id).maybeSingle() : ({ data: null, error: null } as never)),
    { enabled: !!user });
  const nichesQ = useSupabaseQuery<{ id: string; name_ru: string }[]>(['niches-all'],
    async () => supabase.from('niches').select('id, name_ru').eq('is_active', true).order('name_ru'));

  const row = rowQ.data;
  const nicheOpts: Opt[] = (nichesQ.data ?? []).map((n) => ({ value: n.id, label: n.name_ru }));

  return (
    <div className="cr-page">
      <h2 style={{ color: 'var(--text)', fontSize: 18, margin: '4px 0 4px' }}>{tr('creator.profile.title', 'Профиль')}</h2>

      {/* Not verified yet: the code card replaces the editors — saving any of them needs
          the verified link (RLS), so showing them would only produce errors. */}
      {!verified && <CreatorClaim pending={pendingClaim} onRefetch={refetch} embedded />}

      {verified && provider && row && (
        <>
          <div style={sectionLabel}>{tr('creator.profile.about', 'О блогере')}</div>
          <div style={card}>
            <FieldRow providerId={provider.id} title={tr('creator.profile.country', 'Страна')} dbField="country" options={COUNTRIES} current={row.country ?? []} onSaved={rowQ.refetch} />
            <FieldRow providerId={provider.id} title={tr('creator.profile.langs', 'Язык контента')} dbField="content_languages" options={LANGS} current={row.content_languages ?? []} onSaved={rowQ.refetch} />
            <FieldRow providerId={provider.id} title={tr('creator.profile.niche', 'Ниша')} dbField="niche_ids" options={nicheOpts} current={row.niche_ids ?? []} searchable isNiche last onSaved={rowQ.refetch} />
          </div>

          <CollabToggle providerId={provider.id} value={row.open_to_collab ?? true} onSaved={rowQ.refetch} />

          <ContactFields providerId={provider.id} onSaved={rowQ.refetch}
            initial={{
              contact_phone: row.contact_phone ?? profQ.data?.phone ?? '',
              contact_whatsapp: row.contact_whatsapp ?? '',
              contact_telegram: row.contact_telegram ?? '',
              contact_email: row.contact_email ?? user?.email ?? '',
            }} />
        </>
      )}

      {igConfigured() && (
        <div style={{ ...card, padding: 14 }}>
          <div style={{ color: 'var(--text)', fontWeight: 600, marginBottom: 6 }}>
            {tr('creator.profile.igConnect', 'Instagram')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>
            {tr('creator.profile.igConnectHint',
              'Подключите свой Instagram — мгновенное подтверждение аккаунта и статистика аудитории (возраст, город, охваты) в вашем медиа-ките.')}
          </div>
          <button style={smallBtn} onClick={() => startIgConnect('creator')}>
            {tr('creator.profile.igConnectBtn', 'Подключить Instagram')}
          </button>
        </div>
      )}

      {verified && <CreatorMenu barter={provider && row ? { providerId: provider.id, value: !!row.accepts_barter, onSaved: rowQ.refetch } : undefined} />}

      <button type="button" onClick={signOut}
        style={{ display: 'block', width: '100%', margin: '18px 0 4px', padding: '11px', borderRadius: 10, background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', color: 'var(--text-2)', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
        {tr('creator.profile.signOut', 'Выйти')}
      </button>
      {/* Account deletion now lives in the support panel («?») — see CreatorApp onDeleteAccount. */}
    </div>
  );
}
